// BILL-02 — activation remedies, READ-ONLY for the merchant (decided 2026-09-18).
//
// Every canonical activation leaves one OPEN review of the legacy window before it. A merchant must
// never waive, charge or credit its own Kernvel charges, so this page (owner/admin) only shows the
// open reviews with their evidence bounds and every receipt. Kernvel staff resolve them on the
// staff-only /internal/billing-reviews page; the backend has no merchant write route at all.
import { useCallback, useEffect, useState } from "react";

import { ApiError, apiGet } from "../api";
import { useAuth } from "../auth";
import type { OpenRemedy, RemediesData, RemedyReceipt } from "../types";

/** q4 (1/10000 USD, whole cents here) -> "$12.34"; integer arithmetic only. */
export function formatQ4Cents(q4: number): string {
  const cents = Math.trunc(q4 / 100);
  return `$${Math.trunc(cents / 100)}.${String(cents % 100).padStart(2, "0")}`;
}

/** "12.34" -> whole-cent q4, or null when not a plain non-negative dollar amount. */
export function dollarsToQ4(value: string): number | null {
  const match = /^(0|[1-9][0-9]{0,8})(?:\.([0-9]{1,2}))?$/.exec(value.trim());
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
  return cents * 100;
}

export function RemedySummary({ remedy }: { remedy: OpenRemedy }) {
  return (
    <div className="dlq-main">
      <span className="dlq-event">Legacy window {remedy.window_starts_at} – {remedy.window_ends_at}</span>
      <span className="dlq-sub">
        Meter evidence: {remedy.meter_scan.replace(/_/g, " ")}, {remedy.meter_leads.length} meter(s) ·
        charge up to {formatQ4Cents(remedy.charge_bound_q4)} · credit up to {formatQ4Cents(remedy.credit_bound_q4)}
        {remedy.provider_environment === "test" ? " · Shopify TEST" : ""}
      </span>
    </div>
  );
}

export function ReceiptSummary({ receipt }: { receipt: RemedyReceipt }) {
  return (
    <>
      <span className={`dlq-status dlq-status-${receipt.status}`}>{receipt.status.replace(/_/g, " ")}</span>
      <div className="dlq-main">
        <span className="dlq-event">
          {receipt.decision} {receipt.decision === "waive" ? "" : formatQ4Cents(receipt.amount_q4)}
        </span>
        <span className="dlq-sub">
          {receipt.note} · {receipt.actor_role === "kernvel_staff" ? "Kernvel" : receipt.actor_role} · receipt{" "}
          {receipt.receipt_digest.slice(0, 12)}
          {receipt.provider_credit_ref ? ` · ${receipt.provider_credit_ref}` : ""}
        </span>
      </div>
    </>
  );
}

export default function Remedies() {
  const { user } = useAuth();
  const canRead = user?.role === "owner" || user?.role === "admin";
  const [data, setData] = useState<RemediesData | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await apiGet<RemediesData>("/billing/remedies"));
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load billing reviews");
    }
  }, []);
  useEffect(() => {
    if (canRead) void load();
  }, [canRead, load]);

  if (!canRead) {
    return (
      <div className="board-page">
        <h1>Billing reviews</h1>
        <p className="cap-note">Billing reviews are visible to the owner or admin role (yours: {user?.role}).</p>
      </div>
    );
  }
  return (
    <div className="board-page">
      <div className="board-header">
        <h1>Billing reviews</h1>
      </div>
      <p className="cap-note">
        Kernvel reviews each of these and resolves it with a receipt (nothing owed, a charge for evidenced
        unbilled usage, or an app credit for an evidenced over-charge). This view is read-only.
      </p>
      {err && <div className="banner-err">{err}</div>}
      <section className="lane">
        <div className="lane-body">
          {!data ? (
            <p className="lane-empty">{err ? "Billing reviews unavailable" : "Loading billing reviews…"}</p>
          ) : data.open.length === 0 ? (
            <p className="lane-empty">No open billing reviews.</p>
          ) : (
            data.open.map((remedy) => (
              <div className="dlq-row" key={remedy.obligation_ref}>
                <RemedySummary remedy={remedy} />
                <span className="dlq-sub">Awaiting Kernvel review</span>
              </div>
            ))
          )}
        </div>
      </section>
      {data && data.resolutions.length > 0 && (
        <section className="lane">
          <h2>Receipts</h2>
          <div className="lane-body">
            {data.resolutions.map((receipt) => (
              <div className="dlq-row" key={receipt.receipt_ref}>
                <ReceiptSummary receipt={receipt} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
